import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React from 'react'

import { IconButton, TextLink } from 'slab'
import { capitalize, conditionallyLocalizeDate } from 'utils'
import { type MembersCoveredParticipant, type MembersEnrollment, type MembersEnrollmentOverviewProgressState } from 'utils/web_api.schemas'

import { enrollmentDisplayHelper } from '../../utils'
import { CarrierLogo } from '../CarrierLogo'
import CoveredDependent from '../CoveredDependent/CoveredDependent'
import EnrollmentProgress from '../EnrollmentProgress/EnrollmentProgress'
import FinancialStatusIndicator from '../FinancialStatusIndicator/FinancialStatusIndicator'
import TrackerSegment from '../TrackerSegment/TrackerSegment'
import { WhoPaysBadge } from '../WhoPaysBadge/WhoPaysBadge'

type EnrollmentDetailsCarouselProps = {
  currentPlanNumber: number,
  totalPlanCount: number,
  canGoBack: boolean,
  canGoForward: boolean,
  handlePrevious: () => void,
  handleNext: () => void
}

type EnrollmentDetailsProps = {
  enrollment: MembersEnrollment,
  isPriorYear: boolean,
  showNavigation: false,
  carouselProps?: EnrollmentDetailsCarouselProps
}
| {
  enrollment: MembersEnrollment,
  isPriorYear: boolean,
  showNavigation: true,
  carouselProps: EnrollmentDetailsCarouselProps
}

const horizontalDivider = <div className='border-b border-gray-200 my-2' />

const renderCoveredParticipant = (enrollment: MembersEnrollment, isPriorYear: boolean, participant: MembersCoveredParticipant) => {
  const today = new Date()
  let participantIsInactive = false
  let participantDetails: React.ReactNode | undefined

  const possessiveParticipant = participant.given_name.endsWith('s') ? `${participant.given_name}'` : `${participant.given_name}'s`
  const makeParticipantMessage = (startOrEndVerb: string, theDate: Date) => {
    return <>{possessiveParticipant} coverage {startOrEndVerb} on <span className='font-bold'>{conditionallyLocalizeDate(theDate)}</span> due to a qualifying life event</>
  }

  if (enrollment.coverage_dates?.coverage_start_date && participant?.coverage_start_date) {
    const enrollmentStartDate = new Date(enrollment.coverage_dates.coverage_start_date)
    const participantStartDate = new Date(participant.coverage_start_date)
    if (participantStartDate > enrollmentStartDate) {
      const alreadyStarted = participantStartDate <= today
      participantIsInactive = !alreadyStarted
      participantDetails = makeParticipantMessage(alreadyStarted ? 'started' : 'will start', participantStartDate)
    }
  }

  // It's more important to show the coverage will end, hence if instead of if-else
  if (enrollment.coverage_dates?.coverage_end_date && participant?.coverage_end_date) {
    const enrollmentEndDate = new Date(enrollment.coverage_dates.coverage_end_date)
    const participantEndDate = new Date(participant.coverage_end_date)
    if (participantEndDate < enrollmentEndDate) {
      const alreadyEnded = participantEndDate < today
      participantIsInactive = alreadyEnded
      participantDetails = makeParticipantMessage(alreadyEnded ? 'ended' : 'will end', participantEndDate)
    }
  }

  return (
    <CoveredDependent
      key={participant.id}
      coveredParticipant={participant}
      isPriorYear={isPriorYear}
      planIsInactive={enrollment.overview?.financial_status === 'inactive'}
      participantIsInactive={participantIsInactive}
      memberId={enrollment.carrier_member_id && participant.relationship === 'self' ? enrollment.carrier_member_id : undefined}
    >
      {participantDetails}
    </CoveredDependent>
  )
}

const EnrollmentDetails: React.FC<EnrollmentDetailsProps> = ({
  enrollment,
  isPriorYear,
  showNavigation,
  carouselProps
}) => {
  const { startDate, endDate, showTracker, showFinancialStatus, showWhoPays } = enrollmentDisplayHelper(enrollment, isPriorYear)

  const smallHeading = enrollment.plan_name || enrollment.carrier_name

  return (
    <div className='w-full flex flex-col gap-4'>
      <span className='heading-lg text-main'>{capitalize(enrollment.coverage_type)}</span>

      {showNavigation && (
        <>
          <div className='w-full body-sm text-gray-500'>You had multiple plans for this year due to qualifying life events</div>
          <div className='w-full flex flex-row justify-start items-center gap-4'>
            <IconButton
              icon={ChevronLeftIcon}
              label='View previous plan'
              onClick={carouselProps.handlePrevious}
              disabled={!carouselProps.canGoBack}
              style={{ opacity: carouselProps.canGoBack ? 1 : 0.3 }}
            />
            <span className='body-sm-bold text-main'>{carouselProps.currentPlanNumber} of {carouselProps.totalPlanCount} plans</span>
            <IconButton
              icon={ChevronRightIcon}
              label='View next plan'
              onClick={carouselProps.handleNext}
              disabled={!carouselProps.canGoForward}
              style={{ opacity: carouselProps.canGoForward ? 1 : 0.3 }}
            />
          </div>
        </>
      )}

      {horizontalDivider}

      <div className='flex flex-row justify-between items-start gap-4'>
        <div className='flex flex-col gap-1'>
          <span className='heading-sm text-main'>{smallHeading}</span>
          {enrollment.plan_name && <span className='body text-gray-500'>{enrollment.carrier_name}</span>}
        </div>
        {enrollment.carrier_image_url && <CarrierLogo src={enrollment.carrier_image_url} />}
      </div>

      {enrollment.coverage_details_url && (
        <div className='flex flex-row justify-start my-2'>
          <TextLink
            href={enrollment.coverage_details_url}
            target='_blank'
            rightIcon={ArrowUpRightIcon}
            color='purple'
          >
            View coverage details
          </TextLink>
        </div>
      )}

      {horizontalDivider}

      <div className='heading-sm-regular text-main'>Plan details</div>

      {showTracker && (
        <div className='flex flex-col gap-2'>
          <div className='body text-gray-500'>Status</div>
          <EnrollmentProgress
            state={enrollment.overview!.progress_state as MembersEnrollmentOverviewProgressState}
            showCaption
            startDate={startDate!}
          />
          <div className='flex gap-1 mt-4'>
            {enrollment.overview?.progress_events?.map((event) => (
              <TrackerSegment
                key={event.label}
                state={event.state}
                label={event.label}
                completionDate={event.completion_date ? new Date(event.completion_date) : undefined}
                progressState={enrollment.overview!.progress_state!}
              />
            ))}
          </div>
        </div>
      )}

      {showFinancialStatus && (
        <div className='flex flex-col gap-2'>
          <div className='body text-gray-500'>Status</div>
          <FinancialStatusIndicator
            status={enrollment.overview!.financial_status!}
            carrierName={enrollment.carrier_name}
            coverageEndDate={endDate}
            showCaption
          />
        </div>
      )}

      <div className='flex flex-col gap-2'>
        <div className='body text-gray-500'>Monthly premium</div>
        <div className='flex flex-row items-center justify-start gap-4'>
          <div className='body text-main'>{enrollment.monthly_premium ? enrollment.monthly_premium.formatted_amount : '-'}</div>
          {showWhoPays && <WhoPaysBadge thatchPays={enrollment.thatch_pays!} />}
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <div className='body text-gray-500'>Coverage period</div>
        <div className='body text-main'>{startDate ? conditionallyLocalizeDate(startDate) : '(Unknown)'} - {endDate ? conditionallyLocalizeDate(endDate) : '(Unknown)'}</div>
      </div>

      {horizontalDivider}

      <div className='heading-sm-regular text-main'>People covered</div>

      <div className='flex flex-col gap-2'>
        {enrollment.covered_participants?.map((participant) => (
          renderCoveredParticipant(enrollment, isPriorYear, participant)
        ))}
      </div>

    </div>
  )
}

export default EnrollmentDetails
